<template>
  <div class="copilot-live-editor flex grow flex-col text-sm lg:overflow-hidden">
    <div class="flex grow flex-col lg:overflow-hidden">
      <div class="grow p-8 lg:overflow-y-auto lg:p-6">
        <div class="mx-auto max-w-xl space-y-4 md:py-4 lg:py-0">
          <div class="grid gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-1 lg:gap-4">
            <Form
              :data="firstNameForm"
              method="PUT"
              endpoint="user"
              autosave
              @saving="onSaving(1)"
              @success="onSuccess(1)"
            >
              <InputGroup
                :label="$t('first_name')"
                name="first_name"
              >
                <TextInput
                  v-model="firstNameForm.first_name"
                  name="first_name"
                  @update:model-value="onFirstNameChange"
                />
              </InputGroup>
            </Form>

            <Form
              :data="lastNameForm"
              method="PUT"
              endpoint="user"
              autosave
              @saving="onSaving(2)"
              @success="onSuccess(2)"
            >
              <InputGroup
                :label="$t('last_name')"
                name="last_name"
              >
                <TextInput
                  v-model="lastNameForm.last_name"
                  name="last_name"
                  @update:model-value="onLastNameChange"
                />
              </InputGroup>
            </Form>
          </div>

          <Form
            :data="phoneForm"
            method="PUT"
            endpoint="user"
            autosave
            @saving="onSaving(3)"
            @success="onSuccess(3)"
          >
            <InputGroup
              :label="$t('phone_number')"
              name="phone"
            >
              <TextInput
                v-model="phoneForm.phone"
                name="phone"
                @update:model-value="onPhoneChange"
              />
            </InputGroup>
          </Form>

          <Form
            :data="logoForm"
            method="PUT"
            endpoint="user"
            autosave
            @saving="onSaving(4)"
            @success="onSuccess(4)"
          >
            <InputGroup
              :label="$t('logo')"
              name="logo"
            >
              <FileInput
                v-model="logoForm.logo"
                name="logo"
                placeholder-key="select_now_or_add_later"
                @update:model-value="onLogoChange"
              />
            </InputGroup>
          </Form>

          <Form
            :data="personalPhotoForm"
            method="PUT"
            endpoint="user"
            autosave
            @saving="onSaving(5)"
            @success="onSuccess(5)"
          >
            <InputGroup
              :label="$t('personal_photo')"
              name="personal_photo"
            >
              <FileInput
                v-model="personalPhotoForm.personal_photo"
                name="personal_photo"
                placeholder-key="select_now_or_add_later"
                @update:model-value="onPersonalPhotoChange"
              />
            </InputGroup>
          </Form>
        </div>
      </div>
      <div
        v-show="isSaving || saved"
        class="mt-auto border-t p-8"
        :class="{ 'bg-green-50': saved }"
      >
        <div
          class="mx-auto max-w-xl"
          :class="saved ? 'text-green-500' : 'text-supplement'"
        >
          <span class="flex items-center space-x-2.5 font-medium">
            <Icon
              v-if="saved"
              icon="check"
              class="text-lg"
            />
            <Icon
              v-else
              icon="spinner-third"
              prefix="duotone"
              spin
              class="text-lg"
            />
            <span>{{ saved ? $t("saved") : `${$t("saving")}...` }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Media } from '~/types/media'

const props = defineProps<{
  firstName: string | null
  lastName: string | null
  phone: string | null
  logo: Media | null
  personalPhoto: Media | null
}>()

const emit = defineEmits(['update:firstName', 'update:lastName', 'update:phone', 'update:logo', 'update:personalPhoto'])

// Forms
const firstNameForm = reactive({ first_name: props.firstName })
const lastNameForm = reactive({ last_name: props.lastName })
const phoneForm = reactive({ phone: props.phone })
const logoForm = reactive({ logo: props.logo })
const personalPhotoForm = reactive({ personal_photo: props.personalPhoto })

const onFirstNameChange = (firstName: string) => emit('update:firstName', firstName)
const onLastNameChange = (lastName: string) => emit('update:lastName', lastName)
const onPhoneChange = (phone: string) => emit('update:phone', phone)
const onLogoChange = (logo: Media | null) => emit('update:logo', logo)
const onPersonalPhotoChange = (personalPhoto: Media | null) => emit('update:personalPhoto', personalPhoto)

// Saving indicator
const saving = ref<{ [formId: string]: boolean }>({})
const isSaving = computed(() => Object.keys(saving.value).length > 0)
const onSaving = (formId: number) => (saving.value[formId] = true)
const onSuccess = (formId: number) => delete saving.value[formId]

const saved = ref(false)
const timer = ref()

watch(isSaving, (value) => {
  if (value) {
    saved.value = false
  } else {
    if (timer.value) {
      clearTimeout(timer.value)
    }

    saved.value = true
    timer.value = setTimeout(() => {
      saved.value = false
      timer.value = null
    }, 1500)
  }
})
</script>

<style>
.copilot-live-editor {
  > fieldset {
    @apply flex grow flex-col overflow-hidden;
  }

  .accordion {
    .accordion-item__header {
      @apply px-6;
    }

    .accordion-item--active .accordion-item__content {
      @apply lg:p-6;
    }
  }
}
</style>
